<template>
    <div>
        <div class="repayment-select">
            <PageTitleBanner :title="$t('m.repaymentTitle')" :showIcon="true"/>
            <RepaymentSelectSkeleton v-if="isLoading" />
            <template v-else>
                <RepayBillingRuleDisplay :repaymentInfo="repaymentInfo" />
                <PaymentMethods
                    v-bind="paymentMethodsViewProps"
                    @update:selectedMethod="selectedMethod = $event"
                    @update:selectedCardId="selectedCardId = $event"
                    @refresh-wallet="getUserWallet"
                    @show-loading="showLoading"
                    @hide-loading="hideLoading"
                    @toast="toast($event)"
                    @guest-required="handleGuestRequired"
                    :enableBindCardPopup="false"
                    @bind-card="handleShowBind"
                    @prefetch-bind-card="handlePrefetchBindCard"
                    @oneclick-add="handleOneclickAdd"
                />
            <PaymentButton
                v-show="showCardConfirmButton"
                @click="submitCardPayment"
            >
                <span>{{ $t('m.confirm') }}</span>
            </PaymentButton>
            <UnifiedPayButton
                v-for="method in paymentMethods"
                :ref="`unifiedPayButton-${method.method}`"
                :key="`${method.gateway}-${method.method}`"
                :availability-resolved="method.isAvailabilityResolved"
                :availability-available="method.isAvailable"
                :payment-gateway="method.gateway"
                :selected-method="method.method"
                :billing-rule="billingRule"
                :device-code="deviceCode"
                :is-total="false"
                :config="paymentConfig"
                @update-available="updatePaymentAvailable"
                @after-payment="handlePaymentResult"
                @show-loading="showLoading"
                @hide-loading="hideLoading"
                @toast="toast"
                v-show="shouldShowUnifiedPayButton(method)"
            />

            <!-- 支付成功提示组件  -->
            <PopupConfirm :show-header="false" :showPopup="isShowSuccessPopup">
                <div class="success-popup">
                    <div class="icon">
                        <svg width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="15.0184" cy="15.0184" r="13.5184" stroke="#58B469" stroke-width="3"/>
                            <path d="M8.35449 15.0081L12.9982 19.6517C13.3887 20.0423 14.0219 20.0423 14.4124 19.6517L21.8888 12.1753" stroke="#58B469" stroke-width="3" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="subtitle">
                        {{ $t('m.paymentSuccessfulSubtitle') }}
                    </div>
                    <div class="content">
                        {{ $t('m.paymentSuccessfulContent') }}
                    </div>
                    <span v-active-touch class="primary-btn" @click="$router.push({path: '/'})">
                        {{ $t('m.goToRental') }}
                    </span>
                </div>
            </PopupConfirm>
            <PopupConfirm :show-header="false" :showPopup="isShowGuestPopup" @hidePopup="isShowGuestPopup = false">
                <div class="guest-popup">
                    <div class="icon">
                        <WarningIcon />
                    </div>
                    <div class="subtitle">
                        {{ $t('m.guestLoginTips') }}
                    </div>
                    <span v-active-touch class="primary-btn" @click="goLogin">
                        {{ $t('m.confirm') }}
                    </span>
                </div>
            </PopupConfirm>

            <!-- 绑卡弹出层组件 -->
            <BindCardPopup
                ref="bindCardPopup"
                :showPopup="isShowBindPopup"
                :isShowBack="isShowBack"
                :cardGatewayId="cardGatewayId"
                :stripeGatewayId="stripeGatewayId"
                :fortisGatewayId="fortisGatewayId"
                :stripe="stripe"
                :deviceCode="deviceCode"
                :customSubmit="true"
                :fortisClientSecret="fortisClientSecret"
                @hidePopup="isShowBindPopup = false"
                @submit-payment="handleNewCardSubmit"
                @show-loading="showLoading"
                @hide-loading="hideLoading"
                @toast="toast($event)"
                @guest-required="handleGuestRequired"
            />
            </template>
        </div>
        <SplashScreen v-if="isShowSplash"></SplashScreen>
    </div>
</template>

<script>
import {getPaymentConfig,
        doRepayRentPayment,
        confirmRepayRentPayment,
        getUnpaidRent,
        confirmPaymentIntent,
        getUserWallet
        } from "../../api/logined";
import {time2Date} from "../../utils/tool";
import PageTitleBanner from "../../components/PageTitleBanner.vue";
import PaymentButton from "./components/PaymentButton.vue";
import UnifiedPayButton from "./components/UnifiedPayButton.vue";
import PopupConfirm from "../../components/popups/PopupConfirm.vue";
import SplashScreen from "../../components/loading/SplashScreen.vue";
import RepaymentSelectSkeleton from "./components/RepaymentSelectSkeleton.vue";
import RepayBillingRuleDisplay from "./components/RepayBillingRuleDisplay.vue";
import PaymentMethods from "./components/PaymentMethods.vue";
import BindCardPopup from "./components/BindCardPopup.vue";
import WarningIcon from "../../components/icons/WarningIcon.vue";
import { paymentService } from "@/services/paymentService";
import {
    getClient3DSConfirmPayload,
    syncClientNextAction,
} from "@/services/clientNextAction";
import { resolveFortisPaymentMethod } from "@/utils/payment";

export default {
    name: "PaymentSelect",
    components: {
        PageTitleBanner,
        PopupConfirm,
        UnifiedPayButton,
        SplashScreen,
        PaymentButton,
        RepaymentSelectSkeleton,
        RepayBillingRuleDisplay,
        PaymentMethods,
        BindCardPopup,
        WarningIcon
    },
    data() {
        return {
            isLoading: true,
            billingRule: {
                currency: 'usd',
                pre_auth_amount: 0,
            },
            stripe: undefined,
            card: undefined,
            clientSecret: undefined,
            rentPaymentId: undefined,
            deviceCode: undefined,
            applePayAvailable: false,
            googlePayAvailable: false,
            selectedMethod: undefined,
            selectedCardId: undefined,
            selectedRechargeMethod: undefined,
            rechargeMode: false, // 是否显示余额充值相关样式
            selectedAmount: undefined,
            isShowRechargePopup: false,
            isShowRepaymentPopup: false,
            rechargeAmounts: [
                {
                    amount: 100,
                    bonus: 5
                },
                {
                    amount: 200,
                    bonus: 15
                },
                {
                    amount: 300,
                    bonus: 25
                },
            ],
            paymentConfigList: [],
            isShowBindPopup: false,
            bindCardValid: false,
            isShowBack: false,
            fortisCreateOrderRes: {},
            loadingFortis: true,
            isShowSplash: false,
            isShowSuccessPopup: false,
            isShowGuestPopup: false,
            cardList: [],
            paymentMethods: [],
            repaymentInfo: {},
            fortisClientSecret: undefined,
        }
    },
    async created() {
        this.isLoading = true;
        this.deviceCode = this.$store.getters['appConfig/device_code'];

        try {
            const hasUnpaid = await this.getUnpaidRent();

            if (!hasUnpaid) {
                console.log('No unpaid rent found, redirecting to home');
                this.$router.push('/');
                return;
            }

            await Promise.all([
                this.getPaymentConfig(),
                this.getUserWallet()
            ]);

        } catch(error) {
            console.error('Initialization error:', error);
            this.$router.push({
                path: '/machine_config_error',
                query: { message: error.message }
            });
        } finally {
            this.isLoading = false;
        }
    },
    async mounted() {
        // 渲染结束后再更新默认选中类
        this.selectAmount(this.rechargeAmounts && this.rechargeAmounts[0].amount)
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    beforeDestroy() {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    },
        watch: {
        async paymentConfigList() {
            if (this.stripeGatewayId) {
                if (!window.Stripe) {
                    try {
                        await this.$loadScript("https://js.stripe.com/v3/");
                    } catch (error) {
                        console.error("Failed to load Stripe script:", error);
                        return;
                    }
                }
                const config = this.paymentConfigList.find(
                    config => config.name === 'stripe'
                );
                const stripeKey = config ? config.config.public_key : '';
                const stripeAccount = config?.config?.connect_account_id;
                try {
                    const stripeOptions = { locale: this.$i18n.locale };
                    if (stripeAccount) {
                        stripeOptions.stripeAccount = stripeAccount;
                    }
                    this.stripe = window.Stripe(stripeKey, stripeOptions);
                } catch (error) {
                    console.error("Stripe init failed:", error);
                    this.stripe = undefined;
                    return;
                }
            }
        }
    },
    computed: {
        showCardConfirmButton() {
            return this.selectedMethod === "card" && !!this.selectedCardId;
        },
        paymentMethodsViewProps() {
            return {
                paymentMethods: this.paymentMethods,
                cardList: this.cardList,
                selectedMethod: this.selectedMethod,
                selectedCardId: this.selectedCardId,
                cardGatewayId: this.cardGatewayId,
                stripeGatewayId: this.stripeGatewayId,
                fortisGatewayId: this.fortisGatewayId,
                stripe: this.stripe,
                deviceCode: this.deviceCode,
            };
        },
        filteredPaymentMethods() {
            return this.paymentMethods.filter(method => method.isAvailable);
        },
        // stripe 网关ID
        stripeGatewayId() {
            const stripeConfig = this.paymentConfigList.find(config => config.name === 'stripe');
            return stripeConfig ? stripeConfig.gateway_id : 0;
        },
        // fortis 网关ID
        fortisGatewayId() {
            const fortisConfig = this.paymentConfigList.find(config => config.name === 'fortis');
            return fortisConfig ? fortisConfig.gateway_id : 0;
        },
        // fortis 商户名
        fortisMerchantName() {
            const fortisConfig = this.paymentConfigList.find(config => config.name === 'fortis');
            return fortisConfig ? fortisConfig.config.merchant_name : '';
        },
        // fortis ID
        fortisMerchantID() {
            const fortisConfig = this.paymentConfigList.find(config => config.name === 'fortis');
            return fortisConfig ? fortisConfig.config.merchant_id : '';
        },
        // fortis 网关ID
        fortisWalletMerchantID() {
            const fortisConfig = this.paymentConfigList.find(config => config.name === 'fortis');
            return fortisConfig ? fortisConfig.config.wallet_merchant_id : '';
        },
        paymentConfig() {
            const fortisConfig = this.paymentConfigList.find(config => config.name === 'fortis');
            return {
                stripe: this.stripe,
                fortisGatewayId: this.fortisGatewayId || 0,
                merchantName: this.fortisMerchantName || (fortisConfig?.config?.merchant_name || ""),
                authJwt: fortisConfig?.config?.googlejwt || "",
                merchantID: this.fortisMerchantID || (fortisConfig?.config?.merchant_id || ""),
                merchantGatewayID: this.fortisWalletMerchantID || (fortisConfig?.config?.wallet_merchant_id || "")
            };
        },
        // 所有可用支付方式数组
        availablePaymentTypes() {
            const paymentTypes = this.paymentConfigList.flatMap(config => config.payment_method_types);
            return [...new Set(paymentTypes)];
        },
        // applePay 支付所对应的网关id
        applePayGatewayId() {
            const applePayConfig = this.paymentConfigList.find(config =>
                config.payment_method_types?.includes('apple_pay') ||
                config.payment_method_types?.includes('web_apple_pay')
            );
            return applePayConfig ? applePayConfig.gateway_id : 0;
        },
        // googlePay 支付所对应的网关id
        googlePayGatewayId() {
            const googlePayConfig = this.paymentConfigList.find(config =>
                config.payment_method_types?.includes('google_pay') ||
                config.payment_method_types?.includes('web_google_pay')
            );
            return googlePayConfig ? googlePayConfig.gateway_id : 0;
        },
        // card 支付所对应的网关id
        cardGatewayId() {
            const cardConfig = this.paymentConfigList.find(config =>
                config.payment_method_types?.includes('card') ||
                config.payment_method_types?.includes('web_card')
            );
            return cardConfig ? cardConfig.gateway_id : 0;
        },
    },
    methods: {
        handleGuestAccessError(error) {
            const code = Number(error && error.code);
            if (code === 50113) {
                this.isShowGuestPopup = true;
                this.isShowBindPopup = false;
                return true;
            }
            if (code === 50114) {
                this.toast(this.$t(`eMsg.${code}`));
                return true;
            }
            return false;
        },
        handlePaymentError(error, fallbackTitle = "tMsg.paymentFailed") {
            if (this.handleGuestAccessError(error)) {
                return;
            }
            this.toast(this.$t(fallbackTitle) + ': ' + error.message, {
                persistent: !!error?.message
            });
        },
        time2Date: time2Date,
        getUnifiedPayButtonRef(method) {
            const ref = this.$refs[`unifiedPayButton-${method}`];
            const button = Array.isArray(ref) ? ref[0] : ref;
            return button;
        },
        async triggerUnifiedPayButton(method) {
            const button = this.getUnifiedPayButtonRef(method);
            if (button && typeof button.handlePayment === "function") {
                await button.handlePayment();
            }
        },
        async handleOneclickAdd() {
            await this.triggerUnifiedPayButton("oneclick");
        },
        shouldShowUnifiedPayButton(method) {
            return (
                !!method &&
                method.isAvailable &&
                this.selectedMethod === method.method &&
                method.method !== "card" &&
                method.method !== "web_card" &&
                method.method !== "oneclick"
            );
        },
        getMethodLabel(method) {
            switch (method) {
                case 'apple_pay':
                case 'web_apple_pay':
                    return this.$t('m.applePay');
                case 'google_pay':
                case 'web_google_pay':
                    return this.$t('m.googlePay');
                case 'card':
                case 'web_card':
                    return this.$t('m.creditOrDebit');
                default:
                    return method;
            }
        },
        handleMethodClick(method) {
            if (method.method === 'card' || method.method === 'web_card') {
                this.handleShowBind(method);
            } else {
                this.selectedMethod = method.method;
            }
        },
        handleSavedCardClick(item) {
            this.selectedMethod = 'card';
            this.selectedCardId = item.id;
        },
        async handleVisibilityChange() {
            if (document.visibilityState === 'hidden') {
                // 用户离开页面
                this.isShowSplash = true;
            } else {
                // 用户返回页面
                try {
                    // 固定显示0.5秒
                    await new Promise(resolve => setTimeout(resolve, 500));
                } finally {
                    this.isShowSplash = false;
                }
            }
        },
        // 获取网关信息
        getPaymentConfig() {
            return getPaymentConfig({ code: this.deviceCode }).then(res => {
                this.paymentConfigList = res.data.config_list || [];
                this.stripe = undefined;
                this.paymentMethods = paymentService.applyEarlyAvailabilityHints(
                    paymentService.generatePaymentMethods(this.paymentConfigList)
                );
            })
        },
        async getUserWallet() {
            const res = await getUserWallet({ exclude_no_reusable: 1 });
            this.cardList = res.data?.list || [];
        },
        async handleShowBind() {
            // 默认不显示返回按钮
            this.isShowBack = false;
            // 若打开绑卡组件时，充值组件正在显示
            // 则关闭充值组件，显示返回按钮
            if(this.isShowRechargePopup) {
                this.isShowRechargePopup = false;
                this.isShowBack = true
            }

            if (this.cardGatewayId === this.fortisGatewayId) {
                try {
                    this.showLoading();
                    await this.doRepay({
                        paymentMethod: 'web_card',
                        gatewayId: this.fortisGatewayId
                    });
                    this.fortisClientSecret = this.clientSecret;
                    this.isShowBindPopup = true;
                } catch (error) {
                    this.handlePaymentError(error, 'tMsg.initPayFail');
                } finally {
                    this.hideLoading();
                }
            } else {
                this.isShowBindPopup = true;
            }
        },
        async handleNewCardSubmit({ gateway, card, event }) {
            try {
                this.showLoading();

                if (gateway === 'fortis') {
                    const paymentMethod = resolveFortisPaymentMethod(event.data);
                    // Fortis 逻辑: BindCardPopup 已经完成了 card.submit() 并在 'done' 事件中返回了 event 数据
                    // 确认支付
                    await this.confirmRepay({
                        paymentMethod,
                        paymentId: event.data.id,
                    });
                } else if (gateway === 'stripe') {
                    // Stripe 逻辑
                    // 1. 创建订单
                    await this.doRepay({
                        paymentMethod: 'card',
                        gatewayId: this.cardGatewayId
                    });

                    if (this.clientSecret || this.rentPaymentId) {
                        // 2. Stripe 确认支付
                        const stripeConfirmPaymentRes = await this.stripeConfirmPayment(card);

                        // 3. 确认 Repay
                        await this.confirmRepay({
                            paymentMethod: stripeConfirmPaymentRes.paymentIntent.payment_method
                        });
                    }
                }

                // 查询是否还有未支付订单
                const isUnpaid = await this.getUnpaidRent();
                if (!isUnpaid) {
                    this.isShowSuccessPopup = true;
                }
                this.isShowBindPopup = false;
            } catch (error) {
                this.handlePaymentError(error);
            } finally {
                this.hideLoading();
            }
        },
        // Stripe 新卡支付 & 第三方支付
        async confirmPaymentIntent({
            gatewayId,
            internalId,
            paymentMethod,
            paymentId,
            reuseStatus = undefined
        }) {
            try {
                const res = await confirmPaymentIntent({
                    gateway_id: gatewayId,
                    internal_id: internalId,
                    payment_method: paymentMethod,
                    payment_id: paymentId,
                    reuse_status: reuseStatus
                });
                console.log(res,'confirmPaymentIntent');
                return res;
            } catch (error) {
                throw error;
            }
        },
        // Stripe 新卡支付 & 第三方支付
        async stripeConfirmPayment(paymentMethodObj, completeCallback = null) {
            try {
                if(!this.clientSecret) {
                    throw new Error('client secret is missing');
                }
                const confirmParams = paymentMethodObj
                    ? { payment_method: paymentMethodObj }
                    : undefined;
                console.log("[3DS][repayment] stripe.confirmCardPayment request", {
                    clientSecret: this.clientSecret,
                    paymentMethod: paymentMethodObj,
                    confirmParams,
                });
                const res = confirmParams
                    ? await this.stripe.confirmCardPayment(
                        this.clientSecret,
                        confirmParams
                    )
                    : await this.stripe.confirmCardPayment(this.clientSecret);
                console.log("[3DS][repayment] stripe.confirmCardPayment response", res);
                if (res.error) {
                    completeCallback && completeCallback('fail')
                    throw res.error;
                } else {
                    completeCallback && completeCallback('success');
                    return res;
                }
            } catch (error) {
                completeCallback && completeCallback('fail');
                throw error;
            }
        },
        // 完成支付
        async doRepay({paymentMethod = undefined, gatewayId = undefined, cardId = undefined}) {
            try {
                const res = await doRepayRentPayment({
                    rent_id: this.repaymentInfo.rent_id,
                    pay_method_type: paymentMethod,
                    gateway_id: gatewayId,
                    card_id: cardId
                });
                syncClientNextAction(this, res);
                this.rentPaymentId = res.data?.rent_payment_id;
                console.log(res,'repaymentRes');
                return res;
            } catch (error) {
                throw error;
            }
        },

        // 确认支付
        async confirmRepay({paymentMethod = undefined, paymentId = undefined, reuseStatus = undefined}) {
            try {
                const res = await confirmRepayRentPayment({
                    rent_payment_id: this.rentPaymentId,
                    payment_method: paymentMethod,
                    payment_id: paymentId,
                    reuse_status: reuseStatus
                });
                console.log(res,'confirmRepaymentRes');
                return res;
            } catch (error) {
                throw error;
            }
        },

        // Stripe / Fortis 绑卡并支付
        async submitNewCardPayment() {
            // Fortis
            if(this.cardGatewayId === this.fortisGatewayId) {
                this.card.submit()
                return
            }
            // Stripe
            try {
                this.showLoading();
                // 完成支付订单
                await this.doRepay({
                    paymentMethod: 'card',
                    gatewayId: this.cardGatewayId
                });

                if(this.clientSecret || this.rentPaymentId) {
                    // Stripe绑卡支付
                    const stripeConfirmPaymentRes = await this.stripeConfirmPayment({card: this.card});

                    // 确认支付
                    await this.confirmRepay({
                        paymentMethod: stripeConfirmPaymentRes.paymentIntent.payment_method
                    })
                }

                // 查询是否还有未支付订单
                const isUnpaid = await this.getUnpaidRent()
                if(!isUnpaid) {
                    this.isShowSuccessPopup = true;
                }
            } catch(error) {
                this.handlePaymentError(error)
            } finally {
                this.hideLoading();
            }
        },
        // Stripe 第三方支付
        async stripeThirdPartyPayment(ev, method) {
            try {
                // 完成支付订单
                await this.doRepay({
                    paymentMethod: method === 'apple_pay' ? 'apple_pay' : 'google_pay',
                    gatewayId: this.stripeGatewayId
                });

                if(this.clientSecret || this.rentPaymentId) {
                    // Stripe第三方支付绑卡
                    await this.stripeConfirmPayment(ev.paymentMethod.id, (status) => ev.complete(status))

                    // 确认支付
                    await this.confirmRepay({
                        paymentMethod: ev.paymentMethod.id,
                        reuseStatus: method === 'apple_pay' ? 1 : undefined
                    })
                }

                // 查询是否还有未支付订单
                const isUnpaid = await this.getUnpaidRent()
                if(!isUnpaid) {
                    this.isShowSuccessPopup = true;
                }
            } catch(error) {
                ev.complete('fail')
                this.handlePaymentError(error)
            } finally {
                this.hideLoading();
            }
        },
        // Fortis 第三方支付
        async fortisThirdPartyPayment({paymentToken, internalId, rentPaymentId}) {
            try{
                this.rentPaymentId = rentPaymentId;
                await this.confirmPaymentIntent({
                    gatewayId: this.fortisGatewayId,
                    internalId: internalId,
                    paymentMethod: paymentToken,
                    reuseStatus: 0
                })
                // 确认支付
                await this.confirmRepay({
                    reuseStatus: 0
                })
                // 查询是否还有未支付订单
                const isUnpaid = await this.getUnpaidRent()
                if(!isUnpaid) {
                    this.isShowSuccessPopup = true;
                }
            } catch(error) {
                this.handlePaymentError(error)
            } finally {
                this.hideLoading()
            }
        },
        // Stripe 使用已有银行卡支付
        async submitCardPayment() {
            try {
                this.showLoading();
                // 租借/弹宝
                const repaymentRes = await this.doRepay({
                    paymentMethod: 'card',
                    cardId: this.selectedCardId,
                });

                // 3DS 验证
                const client3DSConfirmPayload =
                    getClient3DSConfirmPayload(repaymentRes);
                if (client3DSConfirmPayload) {
                    console.log("[3DS][repayment] doRepay requires 3DS", {
                        rentPaymentId: repaymentRes?.data?.rent_payment_id,
                        payload: client3DSConfirmPayload,
                    });
                    syncClientNextAction(this, repaymentRes);
                    const stripeConfirmPaymentRes = await this.stripeConfirmPayment(
                        client3DSConfirmPayload.paymentMethod
                    );
                    await this.confirmRepay({
                        paymentMethod:
                            stripeConfirmPaymentRes?.paymentIntent?.payment_method ||
                            client3DSConfirmPayload.paymentMethod,
                    });
                }
                // 查询是否还有未支付订单
                const isUnpaid = await this.getUnpaidRent()
                if(!isUnpaid) {
                    this.isShowSuccessPopup = true;
                }
            } catch(error) {
                this.handlePaymentError(error)
            } finally {
                this.hideLoading();
            }
        },

        // 查询是否有未支付订单
        async getUnpaidRent() {
            const res = await getUnpaidRent()
            if(res.data.have_unpaid) {
                this.$store.dispatch('setRepaymentInfo', res.data)
                this.repaymentInfo = res.data;
                return true
            }
            this.$store.dispatch('setRepaymentInfo', {})
            return false
        },

        // 更新第三方支付按钮为可用
        updatePaymentAvailable(walletType, available, meta = {}){
            const paymentMethod = this.paymentMethods.find(
                (method) => method.method === walletType
            );
            if (paymentMethod) {
                paymentMethod.isAvailable = available;
                paymentMethod.isAvailabilityChecking = !!meta.checking;
                paymentMethod.isAvailabilityResolved = !!meta.resolved;
            }
        },
        handlePrefetchBindCard() {
            this.$refs.bindCardPopup?.preloadBindCard({
                prepareIntent: false,
            });
        },
        handleGuestRequired() {
            this.isShowGuestPopup = true;
            this.isShowBindPopup = false;
        },
        goLogin() {
            this.isShowGuestPopup = false;
            this.$store.dispatch('setToken', '');
            this.$router.push({ path: '/login' });
        },
        // 统一处理钱包/第三方支付回调
        handlePaymentResult(data) {
            if (!data) return;
            const { provider, method, ev, paymentToken } = data;
            if (provider === 'fortis' || paymentToken) {
                this.fortisThirdPartyPayment({ paymentToken, internalId: data.internalId, rentPaymentId: data.rentPaymentId });
                return;
            }
            if (provider === 'stripe' && ev) {
                this.stripeThirdPartyPayment(ev, method);
            }
        },
        // 显示充值弹窗
        handleShowRecharge(retain) {
            // 若打开充值组件时，绑卡组件正在显示
            // 则直接关闭绑卡组件
            if(this.isShowBindPopup) {
                this.isShowBindPopup = false
            }
            if(!retain) {
                // 恢复充值金额的默认选中
                this.selectAmount(this.rechargeAmounts && this.rechargeAmounts[0].amount)
                // 恢复支付方式的无选中
                this.selectedRechargeMethod = null;
            }
            this.isShowRechargePopup = true
        },
        // 显示绑卡充值弹窗
        // handleShowBind 已经迁移到了上面，这里应该删除旧的实现
        // 并且保留 selectAmount 方法
        selectAmount(amount) {
            this.selectedAmount = amount
        },
    }
}
</script>

<style lang="scss" scoped>
    @use "@/styles/variables.scss" as *;

    .repayment-select {
        display: flex;
        flex-direction: column;
        background: #f6f6f6;
        color: #333333;
        padding-bottom: 110px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        overflow-y: auto;
    }
    .repayment-select .payment-btn {
        width: 343px;
        position: fixed;
        bottom: 30px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 20px;
            margin-right: 5px;
            margin-bottom: 2px;
        }

        &.invalid {
            background: #E3E3E3;
            color: #A9A9A9;
            pointer-events: none;
        }
    }

    .success-popup {
        display: flex;
        flex-direction: column;
        align-items: center;
        .icon svg {
            width: 24px;
            height: 24px;
        }
        .subtitle {
            font-family: SF Pro Display;
            font-weight: 500;
            font-size: 18px;
            line-height: 25.2px;
            color: #202124;
            margin-bottom: 4px;
        }
        .content {
            font-size: 16px;
            line-height: 23.04px;
            color: #666666;
            text-align: center;
        }
        .primary-btn {
            width: 100%;
            margin-top: 20px;
        }
    }

    .guest-popup {
        display: flex;
        flex-direction: column;
        align-items: center;

        .icon svg {
            width: 26px;
            height: 26px;
        }

        .subtitle {
            font-family: SF Pro Display;
            font-weight: 500;
            font-size: 18px;
            line-height: 25.2px;
            color: #202124;
            margin-bottom: 4px;
            text-align: center;
        }

        .primary-btn {
            width: 100%;
            margin-top: 20px;
        }
    }
</style>
